import { ChevronRight, QrCode01 } from '@untitledui/icons'
import { type JSX, type PropsWithChildren, type ReactNode } from 'react'
import { usePress } from 'react-aria'
import { twJoin } from 'tailwind-merge'

import { createArrayWithLength } from '@/lib/utils'
import ProductBarcodeView from '@components/ProductBarcodeView'
import ProductImage from '@components/ProductImage'
import { useDialog } from '@components/designSystem/Dialog/useDialog'
import { BadgeWithIcon } from '@components/designSystem/badges/badges'

import type { ProductWrapper } from '@state/product'

interface ProductCardProps {
  product: ProductWrapper
  onClick?: () => void
  isSelected?: boolean
  badges?: JSX.Element[]
  utilities?: JSX.Element[]
  footer?: ReactNode
}

function ProductCardShell({ children, onClick }: PropsWithChildren & Pick<ProductCardProps, 'onClick'>) {
  const isInteractive = Boolean(onClick)
  const className = twJoin(
    'relative flex flex-col overflow-hidden rounded-xl p-2 pl-2 shadow-md',
    'border border-border-secondary',
    'shadow-sm',
    'bg-bg-primary',
    isInteractive && 'cursor-pointer hover:bg-primary_hover hover:shadow-md'
  )

  return (
    <div className={className}>
      {children}
    </div>
  )
}

export function ProductCard({ product, onClick, badges, utilities = [], isSelected = false, footer }: ProductCardProps) {
  return (
    <ProductCardShell onClick={onClick}>
      <div
        data-testid='product-card'
        data-gtin={product.gtin}
        className='flex flex-row gap-4'
        role='option'
        aria-selected={isSelected}
        onClick={onClick}
      >
        <div className='w-1/4'>
          <ProductImage src={product.imageUrl} />
        </div>

        <div className='flex-1'>
          <div className='text-md font-medium'>{product.name}</div>
          <div className='text-sm text-text-secondary'>{product.brandName}</div>

          <div className='my-2 flex flex-row flex-wrap gap-2'>
            {badges}
          </div>
        </div>

        <div className="flex items-center sm:hidden">
          <ChevronRight onClick={onClick} />
        </div>

        {utilities}
      </div>
      {footer}
    </ProductCardShell>
  )
}

function ProductCardSkeleton() {
  return (
    <ProductCardShell>
      <div className='flex flex-row gap-4' data-testid='product-card-skeleton'>
        <div className='w-1/4'>
          <ProductImage.Skeleton />
        </div>

        <div className='flex flex-1 flex-col gap-2'>
          <div className="w-7/8 animate-pulse rounded-sm bg-gray-200">&nbsp;</div>
          <div className="w-1/2 animate-pulse rounded-sm bg-gray-200">&nbsp;</div>
          <div className="w-3/4 animate-pulse rounded-sm bg-gray-200">&nbsp;</div>
        </div>
      </div>
    </ProductCardShell>
  )
}

interface LoadingProductCardListProps {
  count?: number
}

export function LoadingProductCardList({ count = 4 }: LoadingProductCardListProps) {
  return (
    <div className='flex flex-col gap-4'>
      {createArrayWithLength(count, (index) => <ProductCard.Skeleton key={index} />)}
    </div>
  )
}

export function ProductBarcodeViewTriggerBadge({ product }: {product: ProductWrapper}) {
  const { showDialog } = useDialog(ProductBarcodeView)
  const { pressProps } = usePress({ onPress: () => showDialog({ product }) })

  return (
    <button {...pressProps} className='cursor-pointer' aria-label='open-gtin-badge'>
      <BadgeWithIcon size='sm' iconTrailing={QrCode01}>
          GTIN
      </BadgeWithIcon>
    </button>
  )
}

ProductCard.Skeleton = ProductCardSkeleton